import { useCallback, useEffect, useState } from "react";
import { createFileRoute, HistoryState, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DarSheetHeader,
  DarSheetEntries,
  type DarSheetPayload,
} from "@/components/dar/DarSheetView";
import {
  getOpenDarSheet as getOpenDarSheetFn,
  submitDarShiftReport as submitDarShiftReportFn,
} from "@/lib/dar.functions";

export const Route = createFileRoute("/_authenticated/dar/review")({
  component: DarReviewPage,
  head: () => ({
    meta: [
      { title: "Review & Submit D.A.R. Sheet — MissionReady" },
      {
        name: "description",
        content:
          "Second verification of every occurrence on your shift D.A.R. Sheet before final submission.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function DarReviewPage() {
  const { currentOrg } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<DarSheetPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const getOpenSheet = useServerFn(getOpenDarSheetFn);
  const submitSheet = useServerFn(submitDarShiftReportFn);

  const load = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    try {
      const sheet = await getOpenSheet({ data: { orgId: currentOrg.id } });
      setView((sheet as DarSheetPayload | null) ?? null);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not load your D.A.R. Sheet");
    } finally {
      setLoading(false);
    }
  }, [currentOrg, getOpenSheet]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    if (!view) return;
    setBusy(true);
    try {
      const report = (await submitSheet({ data: { sheetId: view.sheet.id } })) as { id: string };
      toast.success("D.A.R. Shift Report submitted successfully.");
      void navigate({
        to: "/dar/$reportId",
        params: { reportId: report.id },
        state: { justSubmitted: true } as HistoryState,
      });
    } catch (e: any) {
      toast.error(e?.message ?? "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/dar">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to sheet
        </Link>
      </Button>

      <h1 className="font-display text-2xl font-bold md:text-3xl">Review &amp; Submit</h1>
      <p className="text-sm text-muted-foreground">
        Read every occurrence in order and confirm the shift report is accurate. Submitting locks
        the sheet — later corrections are recorded as amendments, and the original recordings stay
        available.
      </p>

      {loading && (
        <div role="status" className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…
        </div>
      )}

      {!loading && !view && (
        <div className="mt-6 rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Nothing to review — no open D.A.R. Sheet.
        </div>
      )}

      {!loading && view && (
        <div className="mt-6 space-y-4">
          <DarSheetHeader view={view} />
          <DarSheetEntries view={view} allowAmend expandAll onChanged={() => void load()} />

          <div className="sticky bottom-20 space-y-3 rounded-xl border border-border bg-card/95 p-4 backdrop-blur md:bottom-4">
            <label htmlFor="dar-confirm-accurate" className="flex items-start gap-3 text-sm">
              <Checkbox
                id="dar-confirm-accurate"
                checked={confirmed}
                onCheckedChange={(v) => setConfirmed(v === true)}
                className="mt-0.5"
              />
              <span>I have reviewed every entry and confirm this report is accurate.</span>
            </label>
            <Button
              className="h-12 w-full"
              disabled={busy || !confirmed || view.entries.length === 0}
              aria-describedby={confirmed ? undefined : "dar-submit-hint"}
              onClick={() => void submit()}
            >
              <Send className="mr-2 h-4 w-4" aria-hidden="true" /> Submit end-of-shift D.A.R. Sheet
            </Button>
            <p aria-live="polite" className="sr-only">
              {busy ? "Submitting your D.A.R. Sheet." : ""}
            </p>
            {!confirmed && (
              <p id="dar-submit-hint" className="sr-only">
                Confirm you have reviewed every entry to enable submission.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
